import type { AgentStateClass } from './AgentState'

import { Logger } from 'besonders-logger'
import { createStore, reconcile } from 'solid-js/store'
import { stateDB } from '../local-state'

const { WARN, LOG, DEBUG, VERBOSE, ERROR } = Logger.setup(Logger.INFO) // eslint-disable-line unused-imports/no-unused-vars

// A folder group — a named grouping of subscriptions (and/or shares).
export interface IFolderGroup {
	id: string // UUID or stable identifier
	name: string
	createdAt: string // ISO timestamp
	isDeleted?: boolean
}
// A member (share or subscription) belonging to a folder.
export interface IFolderGroupMember {
	folderID: string // FK → IFolderGroup.id
	memberID: string // FK → IShare.id | ISubscription.id
	memberType: 'share' | 'subscription'
	addedAt: string // ISO timestamp
}

// Module-scoped Solid stores so consumers in components react to mutations.
// (Replaces the reactivity that the old MobX→Solid external-source bridge gave us.)
const [_folderGroups, setFolderGroupsStore] = createStore<IFolderGroup[]>([])
const [_folderGroupMembers, setFolderGroupMembersStore] = createStore<IFolderGroupMember[]>([])

export class AgentFolderGroups {
	mixed = this as unknown as AgentStateClass
	get folderGroups(): IFolderGroup[] { return _folderGroups }
	get folderGroupMembers(): IFolderGroupMember[] { return _folderGroupMembers }

	get folderGroupsMap() {
		return new Map(this.folderGroups.map(fg => [fg.id, fg]))
	}

	get folderMembersMap() {
		const map = new Map<string, IFolderGroupMember[]>()
		for (const member of this.folderGroupMembers) {
			const arr = map.get(member.folderID)
			if (arr) {
				arr.push(member)
			} else {
				map.set(member.folderID, [member])
			}
		}
		return map
	}

	/** Return the folder groups that contain the given subscription/share id. */
	getFoldersForSub = async (memberID: string): Promise<IFolderGroup[]> => {
		const members = this.folderGroupMembers.filter(m => m.memberID === memberID)
		const folderIDs = new Set(members.map(m => m.folderID))
		return this.folderGroups.filter(fg => folderIDs.has(fg.id))
	}

	/** Return the member ids for a given folder. */
	getSubsInFolder = async (folderID: string): Promise<IFolderGroupMember[]> => {
		return this.folderGroupMembers.filter(m => m.folderID === folderID)
	}

	addFolder = async function (this: AgentStateClass, folder: IFolderGroup) {
		LOG(`Adding folder:`, folder)
		await stateDB.folderGroups.add(folder)
		this.reloadFolderGroups()
	}

	updateFolder = async function (this: AgentStateClass, id: string, changes: Partial<Omit<IFolderGroup, 'id'>>) {
		const idx = _folderGroups.findIndex(p => p.id === id)
		if (idx === -1) throw ERROR(`[updateFolder] id not found:`, id, changes)
		LOG(`Updating folder:`, id, changes, _folderGroups[idx])
		await stateDB.folderGroups.update(id, changes)
		setFolderGroupsStore(idx, changes)
	}

	/** Delete a folder and cascade-remove all its members. */
	deleteFolder = async function (this: AgentStateClass, folderID: string) {
		const folder = this.folderGroups.find(p => p.id === folderID)
		LOG(`Deleting folder:`, folderID, folder)
		if (!folder) throw ERROR(`Trying to delete non-existing folder:`, folderID)
		await stateDB.folderGroups.delete(folderID)
		await stateDB.folderGroupMembers.where('folderID').equals(folderID).delete()
		this.reloadFolderGroups()
	}

	/** Add the member if absent, remove it if present. */
	toggleFolderMember = async function (this: AgentStateClass, folderID: string, memberID: string, memberType: 'share' | 'subscription') {
		const existing = await stateDB.folderGroupMembers.get([folderID, memberID])
		if (existing) {
			LOG(`Removing member from folder:`, folderID, memberID)
			await stateDB.folderGroupMembers.where({ folderID, memberID }).delete()
		} else {
			LOG(`Adding member to folder:`, folderID, memberID)
			await stateDB.folderGroupMembers.add({ folderID, memberID, memberType, addedAt: new Date().toISOString() })
		}
		this.reloadFolderGroups()
	}

	reloadFolderGroups = async () => {
		const folderGroups = (await stateDB.folderGroups.orderBy('createdAt').toArray()).filter(fg => !fg.isDeleted)
		const folderGroupMembers = await stateDB.folderGroupMembers.orderBy('addedAt').toArray()
		// `reconcile` keeps stable identity for items already present (matched by id for folders, [folderID+memberID] for members),
		// so <For> reuses existing rows instead of remounting on every reload.
		setFolderGroupsStore(reconcile(folderGroups, { key: 'id' }))
		setFolderGroupMembersStore(reconcile(folderGroupMembers))
		DEBUG(`Loaded from IDB:`, { folderGroups: this.folderGroups, folderGroupMembers: this.folderGroupMembers, stateDB })
	}
}
